import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PhoneIncoming } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSoftphone } from '../../app/softphone';
import { toast } from '../../components/toasts';
import { Button } from '../../components/ui';
import { errorMessage, get, post } from '../../lib/api';
import { formatDuration, formatPhone } from '../../lib/format';
import { useContact } from '../../lib/queries';
import { WaitingCall } from '../../lib/types';

/** Contagem ao vivo do tempo de espera. */
function useNow(): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  return now;
}

/**
 * Ligações esperando na fila do PBX, no topo da aba Fila (prioridade sobre o WhatsApp). Quem
 * atende é o PBX, tocando para os disponíveis: aqui o operador enxerga a fila e o tempo de espera.
 */
export function WaitingCalls() {
  const calls = useQuery({ queryKey: ['calls', 'waiting'], queryFn: () => get<WaitingCall[]>('/telephony/queue/waiting'), refetchInterval: 30_000 }).data ?? [];
  const now = useNow();
  if (calls.length === 0) return null;
  return (
    <div className="border-b-2 border-warn/60 bg-warn/5">
      <div className="flex items-center justify-between px-3 pt-2 pb-1 text-[11px] font-semibold tracking-wide text-warn uppercase">
        <span className="flex items-center gap-1">
          <PhoneIncoming className="size-3.5" /> Ligações esperando ({calls.length})
        </span>
        <span className="font-normal normal-case">toca para os disponíveis, ou clique em Atender</span>
      </div>
      {calls.map((c) => (
        <WaitingRow key={c.id} call={c} now={now} />
      ))}
    </div>
  );
}

function WaitingRow({ call, now }: { call: WaitingCall; now: number }) {
  const contact = useContact(call.contactId).data;
  const qc = useQueryClient();
  const softphone = useSoftphone();
  const [busy, setBusy] = useState(false);
  /** Puxa o cliente da fila para o meu ramal; o telefone do navegador atende sozinho. */
  const pickup = async () => {
    setBusy(true);
    if (softphone.status === 'registered' && !softphone.call) softphone.engine.autoAnswerNext(20_000);
    try {
      await post(`/telephony/calls/${call.id}/pickup`);
      void qc.invalidateQueries({ queryKey: ['calls'] });
    } catch (err) {
      softphone.engine.autoAnswerNext(0);
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  };
  const waited = call.queuedAt ? Math.max(0, Math.floor((now - Date.parse(call.queuedAt)) / 1000)) : 0;
  const number = call.phone ? formatPhone(call.phone) : (call.fromNumber ?? 'número oculto');
  return (
    <div className="flex items-center gap-2 border-t border-warn/20 px-3 py-2">
      <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-warn/20 text-warn">
        <PhoneIncoming className="size-3.5" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">{contact?.name ?? number}</div>
        <div className="truncate text-xs text-fg-soft">
          {contact?.name ? number : 'Número sem cadastro'}
          {contact?.agidesk.customer && ` · ${contact.agidesk.customer.name}`}
        </div>
      </div>
      <div className="text-right">
        <div className={waited >= 120 ? 'font-mono text-sm font-semibold text-danger' : 'font-mono text-sm font-semibold text-warn'}>{formatDuration(waited)}</div>
        {call.ringing.length > 0 && <div className="text-[10px] text-fg-soft">tocando</div>}
      </div>
      <Button size="sm" variant="success" loading={busy} disabled={Boolean(softphone.call)} onClick={() => void pickup()} title="Tirar este cliente da fila e atender no seu ramal">
        Atender
      </Button>
    </div>
  );
}
